<template>
  <div class="stakeholder-page">
    <b-container class="stakeholder-shell">
      <section class="hero-panel">
        <div class="hero-copy">
          <p class="eyebrow">Stakeholder Inventory</p>
          <h1>Explore a clearer, more polished gateway into the stakeholder database.</h1>
          <p class="hero-description">
            The stakeholder inventory identifies, profiles, and organizes the health actors that
            support the Department of Health Planning, Research and Statistics across its divisions.
          </p>

          <div class="hero-highlights">
            <div class="highlight-card">
              <span class="highlight-label">Focused Directory</span>
              <p>Browse divisions with clearer descriptions and a more guided starting point.</p>
            </div>
            <div class="highlight-card">
              <span class="highlight-label">Organized Access</span>
              <p>Move from overview to division-level stakeholder records without friction.</p>
            </div>
          </div>
        </div>

        <div class="hero-side">
          <div class="hero-stat">
            <span class="hero-stat__value">{{ sections.length || '--' }}</span>
            <span class="hero-stat__label">Division sections</span>
          </div>
          <div class="hero-note">
            Stakeholders here include organizations, partners, and other entities involved in
            supporting DHPRS activities across the Ministry.
          </div>
        </div>
      </section>

      <section class="content-panel">
        <div class="content-grid">
          <article class="info-card">
            <p class="card-kicker">Purpose</p>
            <h2>Why this inventory matters</h2>
            <p>
              This resource helps teams see who supports each division, where those efforts are
              concentrated, and how stakeholder engagement aligns with broader health planning
              priorities.
            </p>
          </article>

          <article class="info-card">
            <p class="card-kicker">Objectives</p>
            <h2>What the database is designed to support</h2>
            <ul class="objective-list">
              <li>
                Provide DHPRS with a continuously updated database of stakeholders and their areas
                of support.
              </li>
              <li>Help ensure stakeholder activities align with the Ministry’s strategic goals.</li>
              <li>
                Support more effective and efficient engagement with stakeholders through the right
                platforms.
              </li>
            </ul>
          </article>
        </div>

        <section class="sections-panel">
          <div class="sections-header">
            <div>
              <p class="card-kicker">Directory Sections</p>
              <h2>Browse stakeholder divisions</h2>
            </div>
            <span class="section-count">{{ sections.length }} sections</span>
          </div>

          <div v-if="loading" class="state-card">
            <b-spinner class="mb-3" />
            <p>Loading stakeholder divisions...</p>
          </div>

          <div v-else-if="loadError" class="state-card state-card--error">
            <p>{{ loadError }}</p>
          </div>

          <div v-else class="section-grid">
            <SectionCard v-for="(section, index) in sections" :key="index" :section="section" />
          </div>
        </section>
      </section>
    </b-container>
  </div>
</template>

<script>
import { sortBy } from 'lodash';
import SectionCard from '../components/homepage/SectionCard.vue';

export default {
  components: { SectionCard },
  data() {
    return {
      loading: false,
      loadError: '',
      sections: [],
      newDesc: [
        {
          description:
            'Coordinate health policy and plan formulation, review and priority settings',
        },
        {
          description:
            'Coordinate and promote all health research for efficient health system development and knowledge sharing',
        },
        {
          description:
            'Coordinate international cooperation on health and collaborations with development partners, NGOs and other health actors',
        },
        {
          description: 'Plan for human resource for health development',
        },
        {
          description:
            'Coordinate monitoring and evaluation of the health system and its performance',
        },
      ],
      sn: [2, 1, 3, 4, 5],
    };
  },
  async mounted() {
    this.loading = true;
    this.loadError = '';

    try {
      const data = await this.$dataset.getAllStakeholdersDivision();
      this.sections = data.map((sec, i) => ({
        name: sec,
        description: this.newDesc[i]?.description || 'No description available',
        sn: this.sn[i],
      }));
      this.sections = sortBy(this.sections, [(o) => o.sn]);
    } catch (error) {
      this.loadError =
        'We could not load the stakeholder divisions right now. Please refresh and try again.';
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.stakeholder-page {
  min-height: 100vh;
  padding: 2rem 0 3rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.15), transparent 24%),
    radial-gradient(circle at bottom right, rgba(13, 34, 20, 0.12), transparent 28%),
    linear-gradient(180deg, #f5f8f5 0%, #eef3ef 45%, #e7eeea 100%);
}

.stakeholder-shell {
  position: relative;
}

.hero-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(280px, 0.9fr);
  gap: 1.4rem;
  padding: 2rem;
  border: 1px solid rgba(16, 46, 25, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.88), rgba(248, 251, 248, 0.72)),
    linear-gradient(135deg, #f5faf5, #edf4ef);
  box-shadow: 0 28px 70px rgba(20, 43, 27, 0.12);
  backdrop-filter: blur(12px);
}

.eyebrow,
.card-kicker {
  margin-bottom: 0.7rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #1f6b31;
}

.hero-panel h1,
.content-panel h2 {
  font-family: Georgia, 'Times New Roman', serif;
}

.hero-panel h1 {
  max-width: 46rem;
  margin-bottom: 0.9rem;
  font-size: clamp(2.2rem, 4vw, 3.5rem);
  line-height: 1.05;
  color: #0f2d17;
}

.hero-description {
  max-width: 42rem;
  margin-bottom: 1.35rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #4e6254;
}

.hero-highlights {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.highlight-card,
.hero-stat,
.hero-note,
.info-card,
.sections-panel {
  border: 1px solid rgba(16, 46, 25, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 36px rgba(20, 43, 27, 0.08);
}

.highlight-card {
  padding: 1.05rem 1.1rem;
}

.highlight-label {
  display: inline-block;
  margin-bottom: 0.45rem;
  font-size: 0.86rem;
  font-weight: 700;
  color: #143a1f;
}

.highlight-card p,
.hero-note,
.info-card p,
.objective-list li,
.state-card p {
  margin-bottom: 0;
  font-size: 0.94rem;
  line-height: 1.65;
  color: #536557;
}

.hero-side {
  display: grid;
  gap: 1rem;
}

.hero-stat {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 12rem;
  padding: 1.5rem;
  background: linear-gradient(155deg, #12381d 0%, #1e5a2c 55%, #30ab4c 100%);
  color: #f8fff8;
}

.hero-stat__value {
  font-size: clamp(2.9rem, 6vw, 4.4rem);
  font-weight: 700;
  line-height: 1;
}

.hero-stat__label {
  margin-top: 0.6rem;
  font-size: 1rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(248, 255, 248, 0.82);
}

.hero-note {
  padding: 1.3rem 1.35rem;
}

.content-panel {
  margin-top: 1.6rem;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem;
  margin-bottom: 1.4rem;
}

.info-card {
  padding: 1.4rem 1.45rem;
}

.info-card h2,
.sections-header h2 {
  margin-bottom: 0.7rem;
  font-size: clamp(1.4rem, 2.2vw, 1.95rem);
  color: #102c18;
}

.objective-list {
  margin: 0;
  padding-left: 1.1rem;
}

.objective-list li + li {
  margin-top: 0.65rem;
}

.sections-panel {
  padding: 1.5rem;
}

.sections-header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.3rem;
}

.section-count {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: rgba(48, 171, 76, 0.12);
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f6b31;
}

.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
}

.section-grid ::v-deep .hover {
  display: block;
}

.section-grid ::v-deep .card {
  width: 100% !important;
  min-height: 16rem;
  margin-bottom: 0;
  border-radius: 22px;
  box-shadow: 0 20px 40px rgba(15, 45, 23, 0.08) !important;
}

.section-grid ::v-deep .hover:hover {
  transform: translateY(-10px);
}

.state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 15rem;
  border: 1px dashed rgba(16, 46, 25, 0.14);
  border-radius: 20px;
  background: rgba(247, 250, 247, 0.9);
  text-align: center;
}

.state-card--error {
  border-color: rgba(167, 44, 36, 0.18);
  background: rgba(255, 245, 244, 0.92);
}

@media (max-width: 991.98px) {
  .stakeholder-page {
    padding: 1.25rem 0 2.2rem;
  }

  .hero-panel,
  .content-grid {
    grid-template-columns: 1fr;
  }

  .hero-highlights {
    grid-template-columns: 1fr;
  }

  .hero-panel {
    padding: 1.5rem;
    border-radius: 24px;
  }

  .sections-panel {
    padding: 1.2rem;
  }
}

@media (max-width: 575.98px) {
  .hero-panel h1 {
    font-size: 2rem;
  }

  .sections-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }

  .section-grid ::v-deep .card {
    min-height: 14rem;
  }
}
</style>
